import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import Flex from 'components/common/Flex';
import Text from 'components/common/Text';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { AppTheme } from 'styles/theme';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import useDepsContainer from 'hooks/useDepsContainer';
import { useMemo } from 'react';
import { PUMPSvg, PumpkinPointSvg } from 'assets/icons';

const UtilityGraph = (props: any) => {
  const { uiStore } = useDepsContainer();
  const theme = useTheme();
  const maxWidth = useMediaQuery(theme.breakpoints.up(1300));
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles({ ...props, maxWidth, mdDown, lgDown });
  const { t, i18n } = useTranslation();
  const cachedT = useMemo(() => t, [t, uiStore.language]);

  const makeArrowLeftArea = index => {
    return (
      <Flex
        width={`100%`}
        centerY
        height={`calc(100% - 1px)`}
        style={{
          transform: `translate(-50%, 50%) rotate(${index * 15}deg)`
        }}
      >
        <Flex width={'50%'}></Flex>
        <Flex borderBottom={'dashed 2px #C0C3D8'} position={'relative'} width={'50%'}>
          <Flex position={'absolute'} bottom={-10} right={-10}>
            <ArrowForwardIosIcon
              style={{
                width: 18,
                height: 18,
                color: '#C0C3D8'
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    );
  };

  const makeArrowRightArea = index => {
    return (
      <Flex
        width={`100%`}
        centerY
        height={`calc(100% - 1px)`}
        style={{
          transform: `translate(50%, 50%) rotate(${-1 * index * 15}deg)`
        }}
      >
        <Flex borderBottom={'dashed 2px #F9C4B8'} width={'50%'}></Flex>
        <Flex width={'50%'}></Flex>
      </Flex>
    );
  };

  return (
    <Flex width={'100%'} position={'relative'} maxWidth={1100} center px={mdDown ? 0 : 4}>
      <Flex className={classes.left} column justifyContent={'flex-end'} flex={1}>
        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP1')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(3)}
          </Flex>
        </Flex>

        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP2')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(2)}
          </Flex>
        </Flex>

        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP3')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(1)}
          </Flex>
        </Flex>

        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP4')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(0)}
          </Flex>
        </Flex>

        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP5')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(-1)}
          </Flex>
        </Flex>

        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP6')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(-2)}
          </Flex>
        </Flex>

        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP7')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(-3)}
          </Flex>
        </Flex>
        <Flex>
          <Flex className={classes.textLeftWrap} width={'80%'}>
            <Text className={classes.leftText}>{cachedT('PumpkinEconomicSystemPUMP8')}</Text>
          </Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowLeftArea(-4)}
          </Flex>
        </Flex>
      </Flex>

      <Flex flex={1} center px={mdDown ? 1 : 2}>
        <Flex column style={{ marginRight: maxWidth ? 30 : '2vw' }}>
          <Text
            color={'#18181B'}
            style={{
              fontFamily: "'Lexend'",
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: mdDown ? '12px' : lgDown ? '16px' : '28px',
              lineHeight: '133%',
              letterSpacing: '-0.5px',
              color: '#18181B',
              textAlign: 'center'
            }}
          >
            {cachedT('EARN')}
          </Text>
          <PUMPSvg
            style={{
              width: maxWidth ? 112 : '10vw',
              height: maxWidth ? 112 : '10vw',
              marginTop: '2vh',
              marginBottom: '2vh'
            }}
          />
          <Text
            color={'#18181B'}
            style={{
              fontFamily: "'Inter'",
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: mdDown ? '12px' : lgDown ? '16px' : '20px',
              lineHeight: '133%',
              letterSpacing: '-0.5px',
              color: '#E85130',
              textAlign: 'center'
            }}
          >
            PUMP
          </Text>
        </Flex>

        <Flex column style={{ marginLeft: maxWidth ? 30 : '2vw' }}>
          <Text
            color={'#E85130'}
            style={{
              fontFamily: "'Lexend'",
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: mdDown ? '12px' : lgDown ? '16px' : '28px',
              lineHeight: '133%',
              letterSpacing: '-0.5px',
              color: '#18181B',
              textAlign: 'center'
            }}
          >
            {cachedT('SPEND')}
          </Text>
          <PumpkinPointSvg
            style={{
              width: maxWidth ? 116 : '10vw',
              height: maxWidth ? 116 : '10vw',
              marginTop: '2vh',
              marginBottom: '2vh'
            }}
          />
          <Text
            color={'#E85130'}
            style={{
              fontFamily: "'Inter'",
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: mdDown ? '12px' : lgDown ? '16px' : '20px',
              lineHeight: '133%',
              letterSpacing: '-0.5px',
              color: '#E85130',
              textAlign: 'center'
            }}
          >
            {cachedT('Points')}
          </Text>
        </Flex>
      </Flex>

      <Flex className={classes.right} column flex={1}>
        <Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowRightArea(3)}
          </Flex>
          <Flex className={classes.textRightWrap} width={'80%'}>
            <Text className={classes.rightText}>{cachedT('PumpkinEconomicSystemPoints1')}</Text>
            <Flex position={'absolute'} bottom={-10} right={-10}>
              <ArrowForwardIosIcon
                style={{
                  width: 18,
                  height: 18,
                  color: '#F9C4B8'
                }}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowRightArea(2)}
          </Flex>
          <Flex className={classes.textRightWrap} width={'80%'}>
            <Text className={classes.rightText}>{cachedT('PumpkinEconomicSystemPoints2')}</Text>
            <Flex position={'absolute'} bottom={-10} right={-10}>
              <ArrowForwardIosIcon
                style={{
                  width: 18,
                  height: 18,
                  color: '#F9C4B8'
                }}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowRightArea(1)}
          </Flex>
          <Flex className={classes.textRightWrap} width={'80%'}>
            <Text className={classes.rightText} pt={2}>
              {cachedT('PumpkinEconomicSystemPoints3')}
            </Text>
            <Flex position={'absolute'} bottom={-10} right={-10}>
              <ArrowForwardIosIcon
                style={{
                  width: 18,
                  height: 18,
                  color: '#F9C4B8'
                }}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowRightArea(0)}
          </Flex>
          <Flex className={classes.textRightWrap} width={'80%'}>
            <Text className={classes.rightText} pt={2}>
              {cachedT('PumpkinEconomicSystemPoints4')}
            </Text>
            <Flex position={'absolute'} bottom={-10} right={-10}>
              <ArrowForwardIosIcon
                style={{
                  width: 18,
                  height: 18,
                  color: '#F9C4B8'
                }}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowRightArea(-1)}
          </Flex>
          <Flex className={classes.textRightWrap} width={'80%'}>
            <Text className={classes.rightText} pt={2}>
              {cachedT('PumpkinEconomicSystemPoints5')}
            </Text>
            <Flex position={'absolute'} bottom={-10} right={-10}>
              <ArrowForwardIosIcon
                style={{
                  width: 18,
                  height: 18,
                  color: '#F9C4B8'
                }}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowRightArea(-2)}
          </Flex>
          <Flex className={classes.textRightWrap} width={'80%'}>
            <Text className={classes.rightText} pt={2}>
              {cachedT('PumpkinEconomicSystemPoints6')}
            </Text>
            <Flex position={'absolute'} bottom={-10} right={-10}>
              <ArrowForwardIosIcon
                style={{
                  width: 18,
                  height: 18,
                  color: '#F9C4B8'
                }}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex>
          <Flex width={'20%'} className={classes.arrowArea}>
            {makeArrowRightArea(-3)}
          </Flex>
          <Flex className={classes.textRightWrap} width={'80%'}>
            <Text className={classes.rightText} pt={2}>
              {cachedT('PumpkinEconomicSystemPoints7')}
            </Text>
            <Flex position={'absolute'} bottom={-10} right={-10}>
              <ArrowForwardIosIcon
                style={{
                  width: 18,
                  height: 18,
                  color: '#F9C4B8'
                }}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

const useStyles = makeStyles((theme: AppTheme) =>
  createStyles({
    left: {},
    right: {},
    leftText: {
      width: '100%',
      // textAlign: 'right',
      fontFamily: "'Nohemi' !important",
      fontStyle: 'normal !important',
      fontWeight: (props: any) => (props.mdDown ? '300 !important' : '300 !important'),
      fontSize: (props: any) =>
        props.mdDown ? '12px !important' : props.lgDown ? '16px !important' : '18px !important',
      lineHeight: '133% !important',
      letterSpacing: '-0.5px !important',
      color: '#18181B !important'
    },
    rightText: {
      fontFamily: "'Nohemi' !important",
      fontStyle: 'normal !important',
      fontWeight: (props: any) => (props.mdDown ? '300 !important' : '300 !important'),
      fontSize: (props: any) =>
        props.mdDown ? '12px !important' : props.lgDown ? '16px !important' : '18px !important',
      lineHeight: '133% !important',
      letterSpacing: '-0.2px !important',
      color: '#E85130 !important'
    },
    textLeftWrap: {
      marginTop: (props: any) => (props.mdDown ? '8px' : '24px'),
      borderBottom: 'dashed 2px #C0C3D8'
    },
    textRightWrap: {
      marginTop: (props: any) => (props.mdDown ? '8px' : '24px'),
      borderBottom: 'dashed 2px #F9C4B8',
      position: 'relative'
    },
    arrowArea: {
      position: 'relative'
    }
  })
);

export default observer(UtilityGraph);
